import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import CampaignDetails from "@/pages/CampaignDetails";
import StartCampaign from "@/pages/StartCampaign";
import CreatorProfile from "@/pages/CreatorProfile";
import Dashboard from "@/pages/Dashboard";
import Categories from "@/pages/Categories";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";
import AiTools from "@/pages/ai/AiTools";
import FundingCalculator from "@/pages/ai/FundingCalculator";
import CampaignCheck from "@/pages/ai/CampaignCheck";
import RewardGenerator from "@/pages/ai/RewardGenerator";
import MarketingAssistant from "@/pages/ai/MarketingAssistant";
import VideoScript from "@/pages/ai/VideoScript";
import ImageGenerator from "@/pages/ai/ImageGenerator";
import AdvisorPage from "@/pages/ai/AdvisorPage";
import { AuthProvider } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminAuditLog from "@/pages/admin/AdminAuditLog";
import AdminEarlyAccess from "@/pages/admin/AdminEarlyAccess";

const PublicApp = () => (
  <AuthProvider>
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/campaign/:id" element={<CampaignDetails />} />
        <Route path="/start" element={<StartCampaign />} />
        <Route path="/creator/:id" element={<CreatorProfile />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/about" element={<About />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/ai" element={<AiTools />} />
        <Route path="/ai/funding-calculator" element={<FundingCalculator />} />
        <Route path="/ai/campaign-check" element={<CampaignCheck />} />
        <Route path="/ai/reward-generator" element={<RewardGenerator />} />
        <Route path="/ai/marketing" element={<MarketingAssistant />} />
        <Route path="/ai/video-script" element={<VideoScript />} />
        <Route path="/ai/image" element={<ImageGenerator />} />
        <Route path="/ai/advisor/:kind" element={<AdvisorPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    <AuthModal />
  </AuthProvider>
);

const AdminApp = () => (
  <AdminAuthProvider>
    <Routes>
      <Route path="/admin" element={<AdminLogin />} />
      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/campaigns" element={<AdminCampaigns />} />
        <Route path="/admin/finance" element={<AdminFinance />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/early-access" element={<AdminEarlyAccess />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/admin/audit-log" element={<AdminAuditLog />} />
      </Route>
    </Routes>
  </AdminAuthProvider>
);

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthProvider><AuthCallback /></AuthProvider>;
  }
  if (location.pathname.startsWith("/admin")) return <AdminApp />;
  return <PublicApp />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
